import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AbsoluteFill, useCurrentScale } from 'remotion';
import {
  ElementPositions,
  SelectionElementType
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { SELECTION_OUTLINE_COLOR } from '@goldcast/player-config/src/constants/Remotion.constants';
import { ResizeHandle, ResizeHandleType } from './ResizeHandle';
import { SelectionElementData, SelectionOutlineAction } from './types';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';

export interface SelectionOutlineProps {
  elementType: SelectionElementType;
  positions: ElementPositions;
  updatePositions: (_newPositions: ElementPositions) => void;
  onOutlineRelease: (_newPositions: ElementPositions) => void;
  allowResize: boolean;
  disableDrag?: boolean;
  data: SelectionElementData;
  minimumWidthPercentage?: number;
  maintainAspectRatio?: boolean;
}

const globalSelectionState = new Map<string, boolean>();

const getElementKey = (elementType: SelectionElementType, data: SelectionElementData): string => {
  if ('elementId' in data) {
    return `${elementType}-${data.elementId}`;
  }
  if ('speakerId' in data) {
    return `${elementType}-${data.speakerId}`;
  }
  return `${elementType}-${data.type}`;
};

export const SelectionOutline: React.FC<SelectionOutlineProps> = ({
  elementType,
  positions,
  updatePositions,
  onOutlineRelease,
  allowResize,
  data,
  disableDrag,
  minimumWidthPercentage,
  maintainAspectRatio
}) => {
  const scale = useCurrentScale();
  const scaledBorder = Math.ceil(2 / scale);
  const outlineRef = useRef<HTMLDivElement>(null);

  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isHorizontalSnapVisible, setIsHorizontalSnapVisible] = useState(false);
  const [isVerticalSnapVisible, setIsVerticalSnapVisible] = useState(false);

  const elementKey = getElementKey(elementType, data);

  const [isSelected, setIsSelectedState] = useState(() => globalSelectionState.get(elementKey) || false);

  const setIsSelected = useCallback(
    (selected: boolean) => {
      globalSelectionState.set(elementKey, selected);
      setIsSelectedState(selected);
    },
    [elementKey]
  );

  useEffect(() => {
    const handleDocumentPointerDown = () => {
      setIsSelected(false);
      EventBus.dispatch(CustomEvents.SelectionOutlineAction, {
        action: SelectionOutlineAction.DESELECT,
        data
      });
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown);
    };
  }, [data]);

  useEffect(() => {
    if (isSelected) {
      EventBus.dispatch(CustomEvents.SelectionOutlineAction, {
        action: SelectionOutlineAction.SELECT,
        elementType,
        positions: {
          top: positions.top,
          left: positions.left
        },
        data
      });
    }
  }, [elementType, isSelected, positions.left, positions.top, data]);

  useEffect(() => {
    const handleGlobalSelectionAction = (payload: {
      action: SelectionOutlineAction;
      elementType?: SelectionElementType;
      data?: SelectionElementData;
    }) => {
      if (payload.action === SelectionOutlineAction.SELECT) {
        const isSameType = payload?.elementType === elementType;
        const isSameData = payload?.data?.type === data?.type;

        if (isSameType && isSameData && payload.data && data) {
          // For elements with elementId (like images, videos, etc)
          if ('elementId' in payload.data && 'elementId' in data) {
            setIsSelected(payload.data.elementId === data.elementId);
          }
          // For elements with speakerId (like speaker labels)
          else if ('speakerId' in payload.data && 'speakerId' in data) {
            setIsSelected(payload.data.speakerId === data.speakerId);
          }
          // For other types (like captions)
          else {
            setIsSelected(true);
          }
        } else {
          setIsSelected(false);
        }
      }
    };

    const listener = EventBus.on(CustomEvents.SelectionOutlineAction, handleGlobalSelectionAction);

    return () => {
      EventBus.off(CustomEvents.SelectionOutlineAction, listener);
    };
  }, [data, elementType]);

  useEffect(() => {
    if (!isDragging) {
      setIsHorizontalSnapVisible(false);
      setIsVerticalSnapVisible(false);
    }
  }, [isDragging]);

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const applyCenteringSnap = useCallback(
    (newTop: number, newLeft: number, elementWidth: number, elementHeight: number) => {
      const elementCenterX = newLeft + elementWidth / 2;
      const elementCenterY = newTop + elementHeight / 2;

      // Check for snap to center (50% = center of container)
      const SNAP_TOLERANCE = 0.5;
      const shouldSnapVertically = Math.abs(elementCenterX - 50) < SNAP_TOLERANCE;
      const shouldSnapHorizontally = Math.abs(elementCenterY - 50) < SNAP_TOLERANCE;

      let adjustedTop = newTop;
      let adjustedLeft = newLeft;

      if (shouldSnapVertically) {
        adjustedLeft = (100 - elementWidth) / 2;
        setIsVerticalSnapVisible(true);
      } else {
        setIsVerticalSnapVisible(false);
      }

      if (shouldSnapHorizontally) {
        adjustedTop = (100 - elementHeight) / 2;
        setIsHorizontalSnapVisible(true);
      } else {
        setIsHorizontalSnapVisible(false);
      }

      return { top: adjustedTop, left: adjustedLeft };
    },
    []
  );

  const isOutlineInwards = useMemo(() => {
    return elementType === SelectionElementType.VIDEO_IN_COMPOSITE;
  }, [elementType]);

  const style: React.CSSProperties = useMemo(() => {
    const shouldShowOutline = (isHovered && !isDragging) || isSelected;

    return {
      width: positions.width,
      height: positions.height,
      left: positions.left,
      top: positions.top,
      position: 'absolute',
      outline: shouldShowOutline ? `${scaledBorder}px solid ${SELECTION_OUTLINE_COLOR}` : undefined,
      cursor: isDragging ? 'grabbing' : shouldShowOutline ? 'grab' : 'pointer',
      userSelect: 'none',
      touchAction: 'none',
      pointerEvents: 'auto',
      zIndex: positions.zIndex,
      outlineOffset: isOutlineInwards ? `-${scaledBorder}px` : undefined
    };
  }, [
    isHovered,
    isDragging,
    isSelected,
    positions.width,
    positions.height,
    positions.left,
    positions.top,
    positions.zIndex,
    scaledBorder,
    isOutlineInwards
  ]);

  const startDragging = useCallback(
    (e: PointerEvent | React.MouseEvent) => {
      const initialX = e.clientX;
      const initialY = e.clientY;

      const parentDimensions = outlineRef.current?.parentElement?.getBoundingClientRect();
      const parentHeight = parentDimensions?.height || 0;
      const parentWidth = parentDimensions?.width || 0;

      let top = positions.top;
      let left = positions.left;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        setIsDragging(true);
        const offsetY = pointerMoveEvent.clientY - initialY;
        const offsetX = pointerMoveEvent.clientX - initialX;

        const relativeChangeInX = (offsetX / parentWidth) * 100;
        const relativeChangeInY = (offsetY / parentHeight) * 100;

        const isConstrainedElement =
          elementType === SelectionElementType.CAPTIONS || elementType === SelectionElementType.SPEAKER_LABEL;
        const currentTop = parseFloat(positions.top as string);
        const currentLeft = parseFloat(positions.left as string);
        const elementWidth = parseFloat(positions.width as string);
        const elementHeight = parseFloat(positions.height as string);

        let newTop: number;
        let newLeft: number;

        if (isConstrainedElement) {
          const maxTop = 100 - elementHeight;
          const maxLeft = 100 - elementWidth;

          newTop = Math.max(0, Math.min(currentTop + relativeChangeInY, maxTop));
          newLeft = Math.max(0, Math.min(currentLeft + relativeChangeInX, maxLeft));
        } else {
          newTop = currentTop + relativeChangeInY;
          newLeft = currentLeft + relativeChangeInX;
        }

        const snappedPosition = applyCenteringSnap(newTop, newLeft, elementWidth, elementHeight);

        top = `${snappedPosition.top}%`;
        left = `${snappedPosition.left}%`;

        updatePositions({
          top,
          left
        });

        EventBus.dispatch(CustomEvents.SelectionOutlineAction, {
          action: SelectionOutlineAction.UPDATE_POSITION,
          elementType,
          position: {
            top,
            left
          },
          data
        });
      };

      const onPointerUp = () => {
        setIsDragging(false);
        onOutlineRelease({ ...positions, top, left });
        window.removeEventListener('pointermove', onPointerMove);
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });

      window.addEventListener('pointerup', onPointerUp, {
        once: true
      });
    },
    [elementType, onOutlineRelease, positions, updatePositions, data, applyCenteringSnap]
  );

  const onPointerDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.button !== 0) {
        return;
      }

      setIsSelected(true);
      if (!disableDrag) {
        startDragging(e);
      }
    },

    [disableDrag, setIsSelected, startDragging]
  );

  const resizeHandles = useMemo(() => {
    if (maintainAspectRatio) {
      return ['top-left', 'top-right', 'bottom-right', 'bottom-left'] as ResizeHandleType[];
    }
    return [
      'top',
      'top-right',
      'right',
      'bottom-right',
      'bottom',
      'bottom-left',
      'left',
      'top-left'
    ] as ResizeHandleType[];
  }, [maintainAspectRatio]);

  return (
    <AbsoluteFill
      onPointerDown={onPointerDown}
      onPointerEnter={onMouseEnter}
      onPointerLeave={onMouseLeave}
      style={style}
      ref={outlineRef}
      id={'selection-outline-' + elementType}
    >
      {isVerticalSnapVisible && (
        <div
          className="fixed left-1/2 h-full w-0.5 bg-deep-orange-500 transition-all duration-75 ease-in-out"
          style={{
            top: 0,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 10
          }}
        />
      )}
      {isHorizontalSnapVisible && (
        <div
          className="fixed top-1/2 h-0.5 w-full bg-deep-orange-500 transition-all duration-75 ease-in-out"
          style={{
            left: 0,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            zIndex: 10
          }}
        />
      )}

      {allowResize && isSelected && (
        <>
          {resizeHandles.map(type => (
            <ResizeHandle
              key={type}
              positions={positions}
              updatePositions={updatePositions}
              type={type}
              outlineRef={outlineRef}
              setIsDragging={setIsDragging}
              onHandleRelease={onOutlineRelease}
              minimumWidthPercentage={minimumWidthPercentage}
              maintainAspectRatio={maintainAspectRatio}
            />
          ))}
        </>
      )}
    </AbsoluteFill>
  );
};
